import * as React from 'react'
import { useState, useRef } from 'react'

import { CheckIcon, ChevronsUpDown, Search } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Account } from '@/core/api/entities/account/account'
import { useAccounts } from '@/core/hooks/account/useAccounts'
import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import { useDebounce } from '@/core/hooks/utils/useDebounce'
import { Button } from '@/shared/components/atoms/Button'
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandItem,
	CommandList,
} from '@/shared/components/atoms/Command'
import { Input } from '@/shared/components/atoms/Input'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/shared/components/atoms/Popover'
import { ScrollArea } from '@/shared/components/atoms/Scroll'
import { cn } from '@/shared/utils/helpers'

type AccountSelectProps = {
	value?: number | string
	onChange: (accountId: number | string) => void
	disabled?: boolean
	visible?: boolean
	className?: string
}

const AccountSelect: React.FC<AccountSelectProps> = ({
	value: selectedAccountId,
	onChange,
	disabled = false,
	visible = false,
	className,
}) => {
	const { t } = useTranslation('common')

	const scrollAreaRef = useRef<HTMLDivElement>(null)
	const [searchValue, setSearchValue] = useState('')

	// Debounce search value with 300ms delay
	const debouncedSearchValue = useDebounce(searchValue, 300)

	// Fetch accounts from API - only when popover is open
	const {
		data: accountsData,
		isLoading,
		isError,
	} = useAccounts({
		config: useAccountConfig({
			params: {
				page: 1,
				limit: 25,
				query:
					debouncedSearchValue.length >= 2 ? debouncedSearchValue : undefined,
			},
		}),
		options: {
			enabled: visible,
			cacheTime: 60000,
			staleTime: 60000,
		},
	})

	const accounts = accountsData?.data || []

	// Find selected account object
	const selectedAccount = accounts.find(
		(account) => account.id.toString() === selectedAccountId?.toString()
	)

	const handleSelect = (account: Account) => {
		onChange(account.id)
		setSearchValue('')
	}

	// Show search hint when user types but less than 2 characters
	const showSearchHint = searchValue.length > 0 && searchValue.length < 2

	if (isError) {
		return (
			<div className="flex items-center justify-center h-12 px-3 text-sm text-destructive bg-card border border-destructive rounded-lg">
				{t('account.user_profile.account.error_loading') ||
					'Error loading accounts'}
			</div>
		)
	}

	return (
		<Popover
			modal
			onOpenChange={(open) => {
				if (!open) {
					setSearchValue('')
				}
			}}
		>
			<PopoverTrigger asChild>
				<Button
					type="button"
					variant="outline"
					className={cn(
						'flex justify-between items-center w-full h-9 px-3 text-left font-normal bg-transparent dark:bg-input/30',
						!selectedAccountId && 'text-muted-foreground',
						className
					)}
					disabled={disabled}
				>
					<div className="flex items-center gap-2">
						{selectedAccount && (
							<span className="text-sm font-medium">
								{selectedAccount.name}
							</span>
						)}
						{!selectedAccount && (
							<span className="text-muted-foreground">
								{t('account.user_profile.account.select')}
							</span>
						)}
					</div>
					<ChevronsUpDown
						className={cn(
							'size-4 opacity-50',
							disabled ? 'hidden' : 'opacity-100'
						)}
					/>
				</Button>
			</PopoverTrigger>
			<PopoverContent
				className="w-[300px] p-0 z-[1001]"
				align="start"
				style={{ zIndex: 1001 }}
			>
				<div className="p-2 border-b border-border">
					<div className="relative">
						<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground size-4" />
						<Input
							value={searchValue}
							onChange={(e) => {
								setSearchValue(e.target.value)
								setTimeout(() => {
									if (scrollAreaRef.current) {
										const viewportElement = scrollAreaRef.current.querySelector(
											'[data-radix-scroll-area-viewport]'
										)
										if (viewportElement) {
											viewportElement.scrollTop = 0
										}
									}
								}, 0)
							}}
							placeholder={
								t('account.user_profile.account.search') || 'Search accounts...'
							}
							className="pl-10"
						/>
					</div>
					{showSearchHint && (
						<p className="text-xs text-muted-foreground mt-1">
							{t('actions.type_at_least_2_characters_to_search') ||
								'Type at least 2 characters to search'}
						</p>
					)}
				</div>
				<Command>
					<CommandList>
						<ScrollArea ref={scrollAreaRef} className="h-72">
							{isLoading && accounts.length === 0 ? (
								<div className="text-center py-6">
									<p className="text-sm text-muted-foreground">
										{t('actions.loading') || 'Loading...'}
									</p>
								</div>
							) : (
								<>
									<CommandEmpty>
										{showSearchHint ? (
											<div className="text-center py-6">
												<p className="text-sm text-muted-foreground">
													{t('actions.type_at_least_2_characters_to_search') ||
														'Type at least 2 characters to search'}
												</p>
											</div>
										) : (
											t('account.user_profile.account.not_found') ||
											'No account found'
										)}
									</CommandEmpty>
									<CommandGroup>
										{accounts.map((account) => (
											<CommandItem
												key={account.id}
												className="gap-2 cursor-pointer"
												onSelect={() => handleSelect(account)}
											>
												<span className="flex-1 text-sm">{account.name}</span>
												<CheckIcon
													className={`ml-auto size-4 ${
														account.id.toString() ===
														selectedAccountId?.toString()
															? 'opacity-100'
															: 'opacity-0'
													}`}
												/>
											</CommandItem>
										))}
									</CommandGroup>
								</>
							)}
						</ScrollArea>
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	)
}

export { AccountSelect }
